if (!customElements.get('testimonials-autoscroll')) {
  class TestimonialsAutoscroll extends HTMLElement {
    constructor() {
      super();
      this.isPaused = false;
      this.isDragging = false;
      this.hasDragged = false;
      this.currentX = 0;
      this.singleSetWidth = 0;
      this.lastTime = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchStartCurrentX = 0;
      this.touchVelocity = 0;
      this.lastTouchX = 0;
      this.lastTouchTime = 0;
      this.isScrollingVertical = null;
      this.animId = null;

      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);

      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);

      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);

      this.animate = this.animate.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.testimonials-autoscroll__track');
      this.originalList = this.querySelector('.testimonials-autoscroll__list');
      if (!this.track || !this.originalList) return;

      this.speedDuration = parseFloat(this.dataset.speed) || 35;
      this.direction = this.dataset.direction === 'right' ? -1 : 1;
      this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

      this.setupClones();
      this.setupEvents();

      // Start animation loop only when visible in viewport to save CPU during page bootup
      this.isInView = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isInView = entry.isIntersecting;
          if (this.isInView) {
            this.lastTime = performance.now();
            if (!this.animId) {
              this.animId = requestAnimationFrame(this.animate);
            }
          } else {
            if (this.animId) {
              cancelAnimationFrame(this.animId);
              this.animId = null;
            }
          }
        });
      }, { rootMargin: '150px 0px' });

      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.animId) cancelAnimationFrame(this.animId);
      if (this.observer) this.observer.disconnect();
      this.cleanupEvents();
    }

    setupClones() {
      this.singleSetWidth = this.originalList.offsetWidth || this.originalList.scrollWidth;

      if (this.track.children.length === 1) {
        for (let i = 0; i < 2; i++) {
          const clone = this.originalList.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a, button, input').forEach((el) => el.setAttribute('tabindex', '-1'));
          this.track.appendChild(clone);
        }
      }

      if (this.direction === -1) {
        this.currentX = this.singleSetWidth;
      }
      this.updateTransform();
    }

    setupEvents() {
      const el = this;

      if (this.pauseOnHover) {
        el.addEventListener('mouseenter', this.handleMouseEnter);
        el.addEventListener('mouseleave', this.handleMouseLeave);
      }

      // Touch events (passive: false to prevent scroll conflicts when swiping sideways)
      el.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      el.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      el.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      el.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });

      // Mouse drag events
      el.addEventListener('mousedown', this.handleMouseDown);
      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);

      // Prevent click navigation during drag
      el.addEventListener('click', (e) => {
        if (this.hasDragged) {
          e.preventDefault();
          e.stopPropagation();
          this.hasDragged = false;
        }
      }, true);

      this.resizeObserver = new ResizeObserver(() => {
        if (this.originalList) {
          this.singleSetWidth = this.originalList.offsetWidth || this.originalList.scrollWidth;
        }
      });
      this.resizeObserver.observe(this.originalList);
    }

    cleanupEvents() {
      const el = this;
      el.removeEventListener('mouseenter', this.handleMouseEnter);
      el.removeEventListener('mouseleave', this.handleMouseLeave);
      el.removeEventListener('touchstart', this.handleTouchStart);
      el.removeEventListener('touchmove', this.handleTouchMove);
      el.removeEventListener('touchend', this.handleTouchEnd);
      el.removeEventListener('touchcancel', this.handleTouchEnd);
      el.removeEventListener('mousedown', this.handleMouseDown);
      window.removeEventListener('mousemove', this.handleMouseMove);
      window.removeEventListener('mouseup', this.handleMouseUp);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }

    handleMouseEnter() {
      this.isPaused = true;
    }

    handleMouseLeave() {
      if (!this.isDragging) {
        this.isPaused = false;
        this.lastTime = performance.now();
      }
    }

    handleTouchStart(e) {
      if (!e.touches || e.touches.length === 0) return;
      this.isDragging = true;
      this.isPaused = true;
      this.hasDragged = false;
      this.isScrollingVertical = null;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartCurrentX = this.currentX;
      this.lastTouchX = e.touches[0].clientX;
      this.lastTouchTime = performance.now();
      this.touchVelocity = 0;
    }

    handleTouchMove(e) {
      if (!this.isDragging || !e.touches || e.touches.length === 0) return;
      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const deltaX = touchX - this.touchStartX;
      const deltaY = touchY - this.touchStartY;

      if (this.isScrollingVertical === null) {
        if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 5) {
          this.isScrollingVertical = true;
          this.isDragging = false;
          this.isPaused = false;
          return;
        } else if (Math.abs(deltaX) > 5) {
          this.isScrollingVertical = false;
        }
      }

      if (this.isScrollingVertical === false) {
        if (e.cancelable) e.preventDefault();

        if (Math.abs(deltaX) > 5) {
          this.hasDragged = true;
        }

        const now = performance.now();
        const dt = Math.max(now - this.lastTouchTime, 1);
        this.touchVelocity = (touchX - this.lastTouchX) / dt;
        this.lastTouchX = touchX;
        this.lastTouchTime = now;

        this.currentX = this.touchStartCurrentX - deltaX;
        this.normalizeCurrentX();
        this.updateTransform();
      }
    }

    handleTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      if (Math.abs(this.touchVelocity) > 0.3) {
        let velocity = this.touchVelocity * 14;
        const decay = () => {
          if (Math.abs(velocity) > 0.4 && !this.isDragging) {
            this.currentX -= velocity;
            velocity *= 0.92;
            this.normalizeCurrentX();
            this.updateTransform();
            requestAnimationFrame(decay);
          } else {
            this.isPaused = false;
            this.lastTime = performance.now();
          }
        };
        requestAnimationFrame(decay);
      } else {
        this.isPaused = false;
        this.lastTime = performance.now();
      }

      setTimeout(() => {
        this.hasDragged = false;
      }, 50);
    }

    handleMouseDown(e) {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.hasDragged = false;
      this.mouseStartX = e.pageX;
      this.mouseStartCurrentX = this.currentX;
      this.track.style.cursor = 'grabbing';
      this.track.style.userSelect = 'none';
    }

    handleMouseMove(e) {
      if (!this.isDragging) return;
      const deltaX = e.pageX - this.mouseStartX;
      if (Math.abs(deltaX) > 4) {
        this.hasDragged = true;
      }
      this.currentX = this.mouseStartCurrentX - deltaX;
      this.normalizeCurrentX();
      this.updateTransform();
    }

    handleMouseUp() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.track.style.cursor = 'grab';
      this.track.style.userSelect = '';
      this.lastTime = performance.now();
      setTimeout(() => {
        this.hasDragged = false;
      }, 50);
    }

    normalizeCurrentX() {
      if (!this.singleSetWidth || this.singleSetWidth <= 0) return;
      while (this.currentX >= this.singleSetWidth * 2) {
        this.currentX -= this.singleSetWidth;
      }
      while (this.currentX < 0) {
        this.currentX += this.singleSetWidth;
      }
    }

    updateTransform() {
      this.track.style.transform = `translate3d(-${this.currentX.toFixed(2)}px, 0, 0)`;
    }

    animate(currentTime) {
      if (!this.lastTime) this.lastTime = currentTime;
      const deltaTime = (currentTime - this.lastTime) / 1000;
      this.lastTime = currentTime;

      if (!this.isPaused && !this.isDragging && this.singleSetWidth > 0) {
        const pixelsPerSecond = (this.singleSetWidth / Math.max(this.speedDuration, 5)) * this.direction;
        this.currentX += pixelsPerSecond * deltaTime;
        this.normalizeCurrentX();
        this.updateTransform();
      }

      this.animId = requestAnimationFrame(this.animate);
    }
  }

  customElements.define('testimonials-autoscroll', TestimonialsAutoscroll);
}
